/**
 * Licensed Materials - Property of IBM
 * IBM Watson Analytics (C) Copyright IBM Corp. 2016, 2018
 * US Government Users Restricted Rights - Use, duplication or disclosure restricted by GSA ADP Schedule Contract with IBM Corp.
 * @module dashboard/filters/pagecontext/PageContextRangeConditions
 * @see PageContextRangeConditions
 */
define([
	'underscore',
	'../../lib/@waca/core-client/js/core-client/ui/core/Class'
], function(_, Class) {

	/**
	 * A helper class that manages the pageContextEntry conditions member.
	 * (Supporting Range/slider Filters)
	 */
	var PageContextRangeConditions = Class.extend({
		init: function(attrs) {
			PageContextRangeConditions.inherited('init', this, attrs);

			_.each(attrs, function(values, keys) {
				this[keys]=values;
			}.bind(this));

			this.api = {
				getFrom:		this.getFrom.bind(this),
				getTo:			this.getTo.bind(this),
				getAttributeUniqueName: this.getAttributeUniqueName.bind(this),
				getRangeMinBound:	this.getRangeMinBound.bind(this),
				getRangeMaxBound:	this.getRangeMaxBound.bind(this),
				getInvert:		this.getInvert.bind(this),
				getValueCaptions:	this.getValueCaptions.bind(this),
				getValueCount:		this.getValueCount.bind(this),
				hasRangeCondition:	this.hasRangeCondition.bind(this),
				isLarger:		this._isLarger.bind(this),
				isSmaller:		this._isSmaller.bind(this),
				isDataPointType: this.isDataPointType.bind(this),
				hasData: this.hasData.bind(this)
			};
		},

		/**
		 * @returns the 'public api' for a PageContextRangeConditions (a pageContextItem as returned by pageContext's getPageContextItems)
		 */
		getAPI: function() {
			return this.api;
		},

		/**
		 * Returns a from value of a from-to range.
		 * If this is a tab filter, the from value can't be less than the global minimum value (global filter on a range for the same item)
		 * @param rangeOptions - (optional) includes the 'subrangeIdx' (for multi-range conditions eg 1-3,5-7 etc) and conditionIdx (for multi-range conditions)
		 * @returns the nth 'from' value ('' if its an open ended range).
		 */
		getFrom: function(rangeOptions) {
			return this._intersectRanges(rangeOptions).from;
		},

		/**
		 * Returns a to value of a from-to range.
		 * If this is a tab filter, the to value can't be greater than the global maximum value (global filter on a range for the same item)
		 * @param rangeOptions - (optional) includes the 'subrangeIdx' (for multi-range conditions eg 1-3,5-7 etc) and conditionIdx (for multi-range conditions)
		 * @returns the nth 'to' value ('' if its an open ended range.
		 */
		getTo: function(rangeOptions) {
			return this._intersectRanges(rangeOptions).to;
		},

		/**
		 * Returns true if there is a defined range condition defined at this conditionIdx (default 0)
		 * @param rangeOptions - (optional) includes the 'subrangeIdx' (for multi-range conditions eg 1-3,5-7 etc) and conditionIdx (for multi-range conditions)
		 * @returns the nth to value ('' if its an open ended range.
		 */
		hasRangeCondition: function(rangeOptions) {
			var fromTo=this._intersectRanges(rangeOptions);
			return (fromTo.from!=='' || fromTo.to!=='');
		},

		/**
		 * The range conditions spec requires an attributeUniqueName to designate which level of a multi-level
		 * hierarchy the filter applies to.
		 * (NOTE: currently, only the first level below the root is supported since ranges are not exposed for multi-level hierarchies in the ui)
		 * @param rangeOptions - (optional) includes the 'subrangeIdx' (for multi-range conditions eg 1-3,5-7 etc) and conditionIdx (for multi-range conditions)
		 * @returns the attributeUniqueName (usu the name of the first level below the root of the hierarchy=the item name.)
		 */
		getAttributeUniqueName: function(rangeOptions) {
			var conditionIdx=(rangeOptions && rangeOptions.conditionIdx) ||0;
			var subrangeIdx=(rangeOptions && rangeOptions.subrangeIdx) || 0;
			if (this.get() && this.get()[conditionIdx]) {
				return this.get()[conditionIdx].attributeUniqueNames[subrangeIdx];
			}
			var globalContextAPI=this.getGlobalContextAPI();
			if (globalContextAPI) {
				return globalContextAPI.getAttributeUniqueName(rangeOptions);
			}
			return null;
		},

		/**
		 * @returns The captions as a commma-separated list from,to
		 */
		getValueCaptions: function() {
			return (this.get()) ? [this.getFrom(), this.getTo()] : [];
		},

		/**
		 * @returns the number of values 2 for a from-to condition.  0 if not specified
		 */
		getValueCount: function() {
			return (this.hasRangeCondition() && this.get() && this.get().length) ? 2 : 0;
		},

		/**
		 * return the minimum bound for this range which is either the overall minimum,
		 * or, if there is a global 'from value' for this tab filter, the global from value.
		 * @param overallMin - the minimum value.
		 * @param idx (optional) - the spec allows for multiple items and ranges but only 1 is currently supported so it is also defaulted to 0.
		 * @param conditionIdx (optional) - the spec allows for multiple range conditions but only 1 is currently supported so it is defaulted to 0.
		 * @returns the maximum of the 'overallMin' and the global from value.
		 */
		getRangeMinBound: function(overallMin, rangeOptions) {

			if (overallMin === undefined) {
				overallMin = 0;
			}

			var globalContextAPI=this.getGlobalContextAPI();


			//deal with null as a seperate case. Different from undefined
			//only equal to null if the query returned no values.
			//If so, use global From by default.
			//TODO refactor query so that it does not filter minMax
			if (overallMin === null){
				if (globalContextAPI && globalContextAPI.getFrom){
					return globalContextAPI.getFrom(rangeOptions) || '0';
				}else{
					return '0';
				}
			}

			if (!globalContextAPI || !globalContextAPI.getFrom || globalContextAPI.getInvert()) {
				return (overallMin.toString() || '');
			} else {
				return globalContextAPI.getFrom(rangeOptions) || (overallMin.toString() || '');
			}
		},

		/**
		 * return the maximum bound for this range which is either the overall maximum,
		 * or, if there is a global 'to value' that corresponds to this tab filter, the global to value.
		 * @param overallMax - the maximum value.
		 * @param rangeOptions - (optional) includes the 'subrangeIdx' (for multi-range conditions eg 1-3,5-7 etc) and conditionIdx (for multi-range conditions)
		 * @returns the minimum of the 'overallMax' and the global to value.
		 */
		getRangeMaxBound: function(overallMax, rangeOptions) {


			if (overallMax === undefined) {
				overallMax = 0;
			}

			var globalContextAPI=this.getGlobalContextAPI();

			//deal with null as a seperate case. Different from undefined
			//only equal to null if the query returned no values.
			//If so, use global From by default.
			//TODO refactor query so that it does not filter minMax
			if (overallMax === null){
				if (globalContextAPI && globalContextAPI.getTo){
					return globalContextAPI.getTo(rangeOptions) || '0';
				}else{
					return '0';
				}
			}

			if (!globalContextAPI || !globalContextAPI.getTo || (globalContextAPI.getInvert())) {
				return (overallMax.toString() || '');
			} else {
				return globalContextAPI.getTo(rangeOptions) || (overallMax.toString() || '');
			}
		},

		/**
		 * return the invert for this range if there is no global context,
		 * if there is a global that corresponds to this tab filter, return the invert of the global.
		 * @returns {boolean} invert
		 */
		getInvert: function() {
			var globalContextAPI = this.getGlobalContextAPI();
			if (!globalContextAPI) {
				return (this.get()) ? this.get()[0] && this.get()[0].invert || false : false;
			}
			return globalContextAPI.getInvert();
		},

		/**
		 * @returns Get the condition (for now, only 1 is supported) as a spec in the form required by the pageContextSpec.
		 */
		getSpec: function() {
			var conditions=[];
			var firstCondition={
				from: [this.getFrom()],
				to: [this.getTo()],
				invert: this.getInvert(),
				attributeUniqueNames: [this.getAttributeUniqueName()]
			};
			conditions.push(firstCondition);
			return conditions;
		},

		/**
		 * If this is a tab filter, we need to be able to acces the related globalContextAPI to access the related from/to information.
		 */
		getGlobalContextAPI: function() {
			if (this._globalContextCb) {
				var globalContext=this._globalContextCb();
				return (globalContext) ? globalContext.getAPI() : null;
			}
			return null;
		},

		/**
		 * Called by the parent pageContext on item initialization if this item is a tab filter and has a global counterpart.
		 * @param globalContextCb - a callback function which can be used to obtain the global counterpart of this tab filter.
		 */
		setGlobalContextCb: function(globalContextCb) {
			this._globalContextCb=globalContextCb;
		},

		/**
		 * used by updateFilterContext when a use has moved range endpoints.
		 * Initially, the range should be set inactive so that its not passed to the card.
		 *
		 * @param updateCondition - A single from to pair with optional additional parameters (levelId and invert) - from an update command.
		 * @param itemId - An itemId to set for the range (normally, same as itemId of the item.)
		 * The conditions must include AT LEAST a 'from', a 'to' or the 'empty' specifier
		 * If empty is set, the range will not be passed to the card.  This is similar to the empty values array set on member filters to mean 'unfiltered'.
		 * @param options - options for model update
		 */
		updateRangeConditions: function(updateCondition, itemId, options) {
			var newRangeCondition=this._initializeRangeCondition(updateCondition, itemId);
			if(newRangeCondition) {
				//For version 1, there is only 1 condition.  In future, this may be a 'push'
				this.set([newRangeCondition], options);
				return true;
			} else {
				this.set([], options);
				return false;
			}
		},

		/**
		 * @returns false always for range
		 */
		isDataPointType: function () {
			return false;
		},

		hasData: function() {
			let conditions = this.get();
			return conditions ? Object.keys(conditions).length > 0 : false;
		},

		/**
		 * Given an updateCondition initialize the conditions with a new range specification.
		 * @param updateCondition - as specified in the updateFilterContext command which is either an empty object (for no condition), or:
		 *						from/to: at least one specified (open ended low bound or upper bound if not specified)
		 *						invert: true for inverted range
		 *						levelId: for multi-level hierarchies, a specific level that the range pertains to.
		 * @param itemId - an item to use as the attribute to set the range against when no levelId (usu same as the itemId).)
		 * @returns the initialized condition or null if this is an empty condition.
		 */
		_initializeRangeCondition: function(updateCondition, itemId) {
			if (updateCondition && !_.isEqual(updateCondition,{})) {
				var rangeCondition={
					from: [updateCondition.from || ''],
					to: [updateCondition.to || '']
				};
				if (updateCondition.from === 0) {
					rangeCondition.from = [0];
				}
				if (updateCondition.to === 0) {
					rangeCondition.to = [0];
				}
				if (updateCondition.invert) {
					rangeCondition.invert=updateCondition.invert;
				}
				//In future when we support multi-level hierarchy ranges, it is possible to set up ranges that are relevant for particular levels.
				//itemIds will have to be specified for that.
				//For flat hierarchies, the attributeUniqueNames are the same as the first hierarchyUniqueName in the hierarchies for this item.
				rangeCondition.attributeUniqueNames=(updateCondition.levelId) ? [updateCondition.levelId] : [itemId];

				this._validateRangeCondition(rangeCondition);
				return rangeCondition;
			}
			return null;
		},

		/**
		 * Ensure that the condition added from an update command has the miniumum....a from/to and attributeUniqueNames.
		 * This is an internal method so no translation is necessary
		 */
		_validateRangeCondition: function(condition) {
			if (!condition.attributeUniqueNames || condition.attributeUniqueNames.length!==1) {
				throw new Error('PAGECONTEXT: Multi-level hierarchies require itemIds to specify which level the range applies to');
			}
			if (!condition.from && !condition.to) {
				throw new Error('PAGECONTEXT: Multi-level hierarchies require from and to');
			}
		},

		/**
		 * @param rangeOptions - (optional) includes the 'subrangeIdx' (for multi-range conditions eg 1-3,5-7 etc) and conditionIdx (for multi-range conditions)
		 * @returns the raw fromTo values for this filter without any adjustments to values made when intersecting with global context.
		 */
		_thisFromTo: function(rangeOptions) {
			var conditionIdx=(rangeOptions && rangeOptions.conditionIdx) || 0;
			var subrangeIdx = (rangeOptions && rangeOptions.subrangeIdx) || 0;
			var thisCondition=(this.get()) ? this.get()[conditionIdx] : null;
			var thisFrom=(thisCondition && thisCondition.from) ? (thisCondition.from[subrangeIdx] || '') : '';
			if (thisCondition && thisCondition.from[0] === 0){
				thisFrom = 0;
			}
			var thisTo=(thisCondition && thisCondition.to) ? (thisCondition.to[subrangeIdx] || '') : '';
			if (thisCondition && thisCondition.to[0] === 0){
				thisTo = 0;
			}

			return {
				from: thisFrom,
				to: thisTo
			};
		},

		/**
		 * Get the intersection of the tab range and the global range according to these rules:
		 * 1) If there is ONLY a tab filter, or if this is a global filter, simply return the from and to values.
		 * 2) If there is a related global filter, the range of the tab filter needs to be restricted to the global range
		 *		(example: user has set tab filter from 10-20, then set the global filter to 15-40...final range is 15-20 for the tab)
		 * 3) If there is a related global filter AND the global filter has been changed to be completely outside the current tab filter return the global range.
		 *		(example: user has set tab filter from 10-20, then set the global filter to 25-40...final range will simply be 25-40)
		 * 4) If there is a related global filter and is inverted, the range of the tab filter needs to be restricted to the inverted global range
		 *		(example: user has set tab filter from 10-20, then set the inverted global filter to 15-40...final range is 10-40 for the tab and tab filter is deemed as inverted)
		 * @param rangeOptions - (optional) includes the 'subrangeIdx' (for multi-range conditions eg 1-3,5-7 etc) and conditionIdx (for multi-range conditions)
		 * @returns the calculated from to as described in the rules above.
		 */
		_intersectRanges: function(rangeOptions) {
			var thisFromTo=this._thisFromTo(rangeOptions);

			//IF there is no related global filter (or this is a global filter), thisFromTo IS the from and to.
			var globalContextAPI=this.getGlobalContextAPI();
			if (!globalContextAPI) {
				return thisFromTo;
			}
			//At this point, we know 'this' is a tab filter.
			var tabFrom=thisFromTo.from;
			var tabTo=thisFromTo.to;
			var globalFrom=globalContextAPI.getFrom(rangeOptions);
			var globalTo=globalContextAPI.getTo(rangeOptions);
			var globalInvert = globalContextAPI.getInvert();
			var finalFrom, finalTo;

			//Restrict the range of the tabFromTo to be inside the globalFromTo
			if (!globalInvert) {
				finalFrom = this._isLarger(tabFrom, globalFrom) ? tabFrom : globalFrom;
				finalTo=this._isSmaller(tabTo, globalTo) ? tabTo : globalTo;
			} else {
				//Global range is inverted. finalFrom should be lower than global's from. and finialTo should be higher thatn global's to
				if (_.isNull(tabFrom) || _.isUndefined(tabFrom) || tabFrom === '') {
					finalFrom = globalFrom;
				} else {
					finalFrom=this._isSmaller(tabFrom, globalFrom) ? tabFrom : globalFrom;
				}

				if (_.isNull(tabTo) || _.isUndefined(tabTo) || tabTo === '') {
					finalTo = globalTo;
				} else {
					finalTo=this._isLarger(tabTo, globalTo) ? tabTo : globalTo;
				}
			}

			//If the final range isn't positive after adjusting, this means that the global filter's range is completely outside the tab filters range.
			//Since this isn't valid, simply set it to the global range.
			if (this._isLarger(finalFrom, finalTo)) {
				finalFrom=globalFrom;
				finalTo=globalTo;
			}

			return {
				from: finalFrom,
				to: finalTo
			};
		},

		/**
		 * return the larger of n1 and n2 as numbers, then as strings.
		 * the special value '' is considered the smallest possible value.
		 * @param n1 - a number or string to compare
		 * @param n2 - a number or string to compare
		 * @returns true if n1 is larger than n2 (numerically if its a number or as strings)
		 */
		_isLarger: function(n1, n2) {
			if (n1  === '') {
				return false;
			}
			if (n2 === '') {
				return true;
			}
			var f1=parseFloat(n1);
			if (!isNaN(f1) && isFinite(n1)) {
				var f2=parseFloat(n2);
				if (!isNaN(f2) && isFinite(n2)) {
					return f1 > f2;
				}
			}
			return n1 > n2;
		},

		/**
		 * return the smaller of n1 and n2 as numbers, then as strings.
		 * the special value '' is considered the largest possible value.
		 * @param n1 - a number or string to compare
		 * @param n2 - a number or string to compare
		 * @returns true if n1 is smaller than n2 (numerically if its a number or as strings)
		 */
		_isSmaller: function(n1, n2) {
			if (n1  === '') {
				return false; // the special value '' is considered the largest possible value.
			}
			return (n2==='' || !this._isLarger(n1, n2));
		}
	});
	return PageContextRangeConditions;
});
